import React, { useState, useEffect, useRef } from "react";
import angleDownSmall7 from '../../assets/images/svg/angle-down-small-7.svg';
import line53 from '../../assets/images/svg/line-5-3.svg';

// Feature icons
import icon1 from '../../assets/images/svg/feat1.svg'
import icon2 from '../../assets/images/svg/feat2.svg'
import icon3 from '../../assets/images/svg/feat3.svg'
import icon4 from '../../assets/images/svg/feat4.svg'

// Work icons
import work1 from '../../assets/images/svg/work1.svg'
import work2 from '../../assets/images/svg/work2.svg'
import work3 from '../../assets/images/svg/work3.svg'

interface FeatureData {
  icon: string;
  title: string;
  text: string;
}

interface WorkData {
  step: number;
  title: string;
  text: string;
  icon: string;
}

interface SectionData {
  title: string;
  type: 'features' | 'how-it-works';
  content: FeatureData[] | WorkData[];
}

interface CollapsibleSectionsProps {
  openHowItWorks?: boolean;
}

const CollapsibleSections: React.FC<CollapsibleSectionsProps> = ({ openHowItWorks = false }) => {
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    "Feature Highlights": false,
    "How it works": false
  });
  const howItWorksRef = useRef<HTMLElement | null>(null);

  const featureData: FeatureData[] = [
    {
      icon: icon1,
      title: "Smart campaign goals",
      text: "Choose to promote tracks, playlists or grow your audience with predefined setups.",
    },
    {
      icon: icon2,
      title: "Let us analyze your track or playlist",
      text: "Upload your audio file and let us suggest the best interests for optimal results!",
    },
    {
      icon: icon3,
      title: "Pixel & tracking support",
      text: "Use your Meta Pixel to track conversions and optimize performance.",
    },
    {
      icon: icon4,
      title: "Simple, guided workflow",
      text: "No Ads Manager chaos – just a step-by-step flow built for artists & curators.",
    },
  ];

  const howItWorksData: WorkData[] = [
    {
      step: 1,
      title: "Connect your Meta account",
      text: "Link your Facebook/Instagram profile and ad account",
      icon: work1,
    },
    {
      step: 2,
      title: "Build & Budget Your Ad",
      text: "Create your ad with our easy builder and choose your budget",
      icon: work2,
    },
    {
      step: 3,
      title: "Launch & Track results",
      text: "Monitor clicks and conversions right from Pitchplaylists!",
      icon: work3,
    },
  ];

  const collapsibleSections: SectionData[] = [
    {
      title: "Feature Highlights",
      type: 'features',
      content: featureData
    },
    {
      title: "How it works",
      type: 'how-it-works',
      content: howItWorksData
    },
  ];

  const toggleSection = (title: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  // Handle external request to open "How it works" section
  useEffect(() => {
    if (openHowItWorks && !expandedSections["How it works"]) {
      setExpandedSections(prev => ({
        ...prev,
        "How it works": true
      }));
      
      // Scroll to the section after a short delay to ensure it's rendered
      setTimeout(() => {
        if (howItWorksRef.current) {
          howItWorksRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [openHowItWorks]);

  return (
    <>
      {/* Top Divider Line */}
      <img
        className="relative ml-[26px] w-[calc(100%-38px)] h-px mt-[-1px] object-cover"
        alt=""
        src={line53}
      />

      {collapsibleSections.map((section, index) => (
        <React.Fragment key={index}>

          <section 
            ref={section.title === "How it works" ? howItWorksRef : null}
            id={section.title === "How it works" ? "how-it-works-section" : undefined}
            className="flex items-start gap-6 relative self-stretch w-full flex-[0_0_auto]">
            {/* Empty spacer to match numbered circle width */}
            <div style={{ width: "34px", height: "32px" }} />

            <div className="gap-6 flex-1 grow flex items-start relative">
              <div className="flex flex-col items-start gap-6 relative flex-1 grow">

                {/* Button Row */}
                <button
                  className="flex w-full items-center justify-between gap-4"
                  onClick={() => toggleSection(section.title)}
                >

                  {/* Left Title */}
                  <h2 className="mt-[-1px] ml-[-8px] opacity-[0.88] font-semibold text-white text-[32px] leading-8 whitespace-nowrap">
                    {section.title}
                  </h2>

                  {/* Right Icon */}
                  <div className="w-10 h-10 flex items-center justify-center">
                    <img
                      className={`w-[18px] transition-transform ${expandedSections[section.title] ? 'rotate-180' : ''}`}
                      alt=""
                      src={angleDownSmall7}
                    />
                  </div>

                </button>

                {/* Content Section */}
                {expandedSections[section.title] && (
                  <div className="w-full">
                    {section.type === 'features' ? (
                      /* Feature Highlights Grid */
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                        {(section.content as FeatureData[]).map((feature: FeatureData, featureIndex: number) => (
                          <div
                            key={featureIndex}
                            className="bg-[#878da11a] border border-[#222] rounded-2xl p-6 flex flex-col gap-4"
                          >
                            <img
                              src={feature.icon}
                              alt={feature.title}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'contain'
                              }}
                            />
                            <h3
                              className="text-white"
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 500,
                                fontStyle: 'medium',
                                fontSize: '18px',
                                lineHeight: '140%',
                                letterSpacing: '0%'
                              }}
                            >
                              {feature.title}
                            </h3>
                            <p
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 400,
                                fontStyle: 'regular',
                                fontSize: '14px',
                                lineHeight: '140%',
                                letterSpacing: '0%',
                                color: '#FFFFFF'
                              }}
                            >
                              {feature.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* How It Works Grid */
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                        {(section.content as WorkData[]).map((item: WorkData, itemIndex: number) => (
                          <div
                            key={itemIndex}
                            className="bg-[#878da11a] border border-[#222] rounded-2xl p-6 flex flex-col gap-4"
                          >
                            <img
                              src={item.icon}
                              alt={item.title}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'contain'
                              }}
                            />
                            <h3
                              className="text-white"
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 500,
                                fontStyle: 'medium',
                                fontSize: '18px',
                                lineHeight: '140%',
                                letterSpacing: '0%'
                              }}
                            >
                              {item.title}
                            </h3>
                            <p
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 400,
                                fontStyle: 'regular',
                                fontSize: '14px',
                                lineHeight: '140%',
                                letterSpacing: '0%',
                                color: '#FFFFFF'
                              }}
                            >
                              {item.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Divider Line Between Items */}
          {index < collapsibleSections.length - 1 && (
            <img
              className="relative ml-[26px] w-[calc(100%-38px)] h-px object-cover"
              alt=""
              src={line53}
            />
          )}

        </React.Fragment>
      ))}

      {/* Bottom Divider Line */}
      <img
        className="relative ml-[26px] w-[calc(100%-38px)] h-px mt-[-1px] object-cover"
        alt=""
        src={line53}
      />
      </>
  );
};

export default CollapsibleSections;